import { useEffect, useRef, useState } from "react";
import "./khorAiSection.css";

const useInView = (threshold = 0.2) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
};

const SCENES = ["upload", "processing", "results"];
const DURATIONS = { upload: 10000, processing: 4800, results: 6000 };

const PROCESSING_STATUSES = [
  "Analyzing Property Info…",
  "Analyzing Rent Roll…",
  "Analyzing Operating Expenses…",
];

const FILES = [
  { ext: "pdf", name: "Offering_Memorandum.pdf" },
  { ext: "docx", name: "Rent_Roll.docx" },
  { ext: "xlsx", name: "T12.xlsx" },
];

const KhorAiSection = () => {
  const [sectionRef, inView] = useInView(0.2);
  const [hasAppeared, setHasAppeared] = useState(false);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);
  const scene = SCENES[sceneIndex];

  useEffect(() => {
    if (inView) setHasAppeared(true);
  }, [inView]);

  useEffect(() => {
    if (!inView) return undefined;
    const timer = setTimeout(() => {
      setSceneIndex((i) => (i + 1) % SCENES.length);
    }, DURATIONS[scene]);
    return () => clearTimeout(timer);
  }, [inView, scene]);

  useEffect(() => {
    if (scene !== "processing" || !inView) {
      // Don't reset here — the processing layer is still mid-fade-out
      // (0.4s CSS transition) when the scene changes, so resetting to 0
      // immediately flashed "Analyzing Property Info…" back on screen
      // right as it disappeared. Leave the last status in place and only
      // reset when a fresh "processing" scene actually begins.
      return undefined;
    }
    setStatusIndex(0);
    const interval = setInterval(() => {
      setStatusIndex((i) => Math.min(i + 1, PROCESSING_STATUSES.length - 1));
    }, 1300);
    return () => clearInterval(interval);
  }, [scene, inView]);

  // CSS keyframes key off data-scene, so only expose the real scene once the
  // section is actually in view — otherwise they'd play once on mount and
  // freeze (via `forwards`) long before the user scrolls down to see them.
  const stageScene = inView ? scene : "idle";

  return (
    <section className="kai-section" ref={sectionRef}>
      <div className={`kai-container kai-fade ${hasAppeared ? "kai-visible" : ""}`}>
        <p className="kai-eyebrow">Khor AI (BETA)</p>
        <h2 className="kai-headline">From source documents to a working model.</h2>
        <p className="kai-subtext">
          Upload any property source documents (offering memo, rent roll, etc.) — Khor{" "}AI
          reads the documents, extracts the inputs, and builds a Khor-powered model in Excel.
        </p>

        <div className="kai-stage" data-scene={stageScene}>
          <div className="kai-incoming" aria-hidden="true">
            {FILES.map((f, i) => (
              <div className={`kai-file-chip kai-file-chip--${i + 1}`} key={f.name}>
                <span className={`kai-file-badge kai-file-badge--${f.ext}`}>{f.ext.toUpperCase()}</span>
                <span className="kai-file-name">{f.name}</span>
              </div>
            ))}
          </div>

          <div className="kai-panel" aria-hidden="true">
            <div className="kai-panel-header">
              <img src="/logo_large.png" alt="" className="kai-panel-logo" />
              Model Builder
            </div>
            <div className="kai-panel-tabs">
              <span className="kai-tab">Standard File Upload</span>
              <span className="kai-tab kai-tab--active">Khor AI (BETA)</span>
            </div>

            <div className="kai-panel-body">
              <div className="kai-layer kai-layer--upload">
                <div className="kai-dropzone">
                  <div className="kai-dropzone-icon">&#8593;</div>
                  <p className="kai-dropzone-text">Click Browse to add file(s)</p>
                  <span className="kai-browse-btn">Browse&#8230;</span>
                </div>

                <div className="kai-file-list">
                  {FILES.map((f, i) => (
                    <div className={`kai-file-row kai-file-row--${i + 1}`} key={f.name}>
                      <span className="kai-file-row-remove">&times;</span>
                      {f.name}
                    </div>
                  ))}
                </div>

                <button className="kai-build-btn" type="button" tabIndex={-1}>
                  Done
                </button>
              </div>

              <div className="kai-layer kai-layer--processing">
                <img src="/logo_large.png" alt="" className="kai-processing-icon" />
                <p className="kai-processing-title">Please Wait:</p>
                <p className="kai-processing-status">{PROCESSING_STATUSES[statusIndex]}</p>
                <div className="kai-progress-track">
                  <div className="kai-progress-fill" />
                </div>
              </div>

              <div className="kai-layer kai-layer--results">
                <div className="kai-result-card">
                  <p className="kai-result-title">Property Info</p>
                  <p className="kai-result-label">Summary</p>
                  <p className="kai-result-text">
                    Inwood Shops is a 191,650 SF retail shopping center located at 124 Oak
                    Street, Leonard, OH 29526, anchored by Target, TJ Max, and Michaels.
                  </p>
                </div>
                <div className="kai-result-card">
                  <p className="kai-result-title">Rent Roll</p>
                  <p className="kai-result-label">Input Highlights</p>
                  <ul className="kai-result-list">
                    <li>Target (Suite 2): 30,000 SF - $9 PSF</li>
                    <li>TJ Max (Suite 4): 24,082 SF - $15.50 PSF</li>
                    <li>Michaels (Suite 6): 23,872 SF - $18 PSF</li>
                  </ul>
                </div>
                <div className="kai-result-card">
                  <p className="kai-result-title">Operating Expenses</p>
                  <p className="kai-result-label">Input Highlights</p>
                  <ul className="kai-result-list">
                    <li>CAM: $1,540 Monthly</li>
                    <li>Taxes: 0.25 $/SF</li>
                    <li>Property Management: 3% of Effective Gross Revenue</li>
                  </ul>
                </div>
                <button className="kai-build-btn" type="button" tabIndex={-1}>
                  Build Khor Model
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="kai-dots">
          {SCENES.map((s, i) => (
            <button
              key={s}
              className={`kai-dot ${i === sceneIndex ? "kai-dot--active" : ""}`}
              onClick={() => setSceneIndex(i)}
              aria-label={`Show ${s} step`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default KhorAiSection;
